import type { FC } from 'react';
import type { BoxForm, Ply } from '../lib/types';
import { VEHICLES, COLOUR_OPTIONS, PACKING_EFFICIENCY } from '../lib/constants';
import { s } from '../lib/styles';
import PlyConfigurator from './PlyConfigurator';

interface Props {
  parseNotes: string | null;
  form: BoxForm;
  onFormChange: (field: keyof BoxForm, value: string) => void;
  plies: Ply[];
  onAddPly: () => void;
  onRemovePly: () => void;
  onUpdatePly: (index: number, field: keyof Ply, value: string) => void;
  printRatePerKg: number;
  onPrintRateChange: (value: number) => void;
  colourOptionIndex: number;
  onColourChange: (index: number) => void;
  pricingError: string | null;
  pricing: boolean;
  onGetQuotes: () => void;
  onBack: () => void;
}

const Page2_BoxConfig: FC<Props> = ({
  parseNotes,
  form,
  onFormChange,
  plies,
  onAddPly,
  onRemovePly,
  onUpdatePly,
  printRatePerKg,
  onPrintRateChange,
  colourOptionIndex,
  onColourChange,
  pricingError,
  pricing,
  onGetQuotes,
  onBack,
}) => (
  <div style={s.body}>
    {/* Parse notes banner */}
    {parseNotes && (
      <div style={s.notesBanner}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>✦</span>
        <span style={{ fontSize: 11, color: '#3730a3', lineHeight: 1.55 }}>
          {parseNotes}
        </span>
      </div>
    )}

    {/* Dimensions + order */}
    <div style={s.card}>
      <div style={{ ...s.cardTitle, marginBottom: 12 }}>
        Box Dimensions &amp; Order
      </div>
      <div style={{ ...s.sectionLabel, marginBottom: 7 }}>📐 Size (cm)</div>
      <div style={s.row3}>
        {(['length', 'width', 'height'] as Array<keyof BoxForm>).map(
          (field) => (
            <div key={field} style={s.fieldGroup}>
              <label style={s.label}>
                {field[0].toUpperCase() + field.slice(1)}
              </label>
              <input
                type="number"
                value={form[field]}
                onChange={(e) => onFormChange(field, e.target.value)}
                placeholder="0"
                style={s.input}
              />
            </div>
          )
        )}
      </div>
      <div style={{ ...s.row2, marginTop: 9 }}>
        <div style={s.fieldGroup}>
          <label style={s.label}>Quantity</label>
          <input
            type="number"
            value={form.quantity}
            onChange={(e) => onFormChange('quantity', e.target.value)}
            placeholder="e.g. 500"
            style={s.input}
          />
        </div>
        <div style={s.fieldGroup}>
          <label style={s.label}>Delivery Location</label>
          <input
            value={form.location}
            onChange={(e) => onFormChange('location', e.target.value)}
            placeholder="e.g. Mumbai"
            style={s.input}
          />
        </div>
      </div>
    </div>

    {/* Vehicle options */}
    <div style={{ ...s.card, background: '#fffbeb', borderColor: '#fde68a' }}>
      <div style={{ ...s.cardTitle, marginBottom: 10, color: '#92400e' }}>
        🚛 Vehicle Options
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2,1fr)',
          gap: 6,
        }}
      >
        {VEHICLES.map((v) => (
          <div
            key={v.name}
            style={{
              background: '#ffffff',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: '7px 10px',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>
              {v.name}
            </div>
            <div style={{ fontSize: 10, color: '#a16207' }}>
              {v.cargo_m3} m³ · ₹{v.rate_per_km}/km
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: '#a16207' }}>
        AI picks the cheapest vehicle that fits the full order in one trip.
        Packing efficiency: {PACKING_EFFICIENCY * 100}%.
      </div>
    </div>

    {/* Print config */}
    <div style={{ ...s.card, background: '#faf5ff', borderColor: '#e9d5ff' }}>
      <div style={{ ...s.cardTitle, marginBottom: 10, color: '#6b21a8' }}>
        🖨 Print Config
      </div>
      <div style={s.row2}>
        <div style={s.fieldGroup}>
          <label style={s.label}>Print rate (₹/kg outer liner)</label>
          <input
            type="number"
            value={printRatePerKg}
            min={0}
            onChange={(e) => onPrintRateChange(Number(e.target.value))}
            style={s.input}
          />
        </div>
        <div style={s.fieldGroup}>
          <label style={s.label}>Colour</label>
          <select
            value={colourOptionIndex}
            onChange={(e) => onColourChange(Number(e.target.value))}
            style={s.input}
          >
            {COLOUR_OPTIONS.map((o, i) => (
              <option key={o.label} value={i}>
                {o.label} (×{o.multiplier})
              </option>
            ))}
          </select>
        </div>
      </div>
      {COLOUR_OPTIONS[colourOptionIndex].multiplier === 0 && (
        <div style={{ marginTop: 8, fontSize: 10, color: '#9333ea' }}>
          No print selected — printing cost will be ₹0.
        </div>
      )}
    </div>

    {/* Ply configurator */}
    <PlyConfigurator
      plies={plies}
      onAdd={onAddPly}
      onRemove={onRemovePly}
      onUpdate={onUpdatePly}
      printRatePerKg={printRatePerKg}
      colourOptionIndex={colourOptionIndex}
    />

    {pricingError && <div style={s.errorBox}>⚠ {pricingError}</div>}

    <div style={{ display: 'flex', gap: 9 }}>
      <button onClick={onBack} style={s.backBtn}>
        ← Back
      </button>
      <button
        onClick={onGetQuotes}
        disabled={pricing}
        style={{ ...s.primaryBtn, flex: 1, opacity: pricing ? 0.6 : 1 }}
      >
        {pricing ? (
          <span style={s.btnRow}>
            <span style={s.spinner} /> Calculating quotes...
          </span>
        ) : (
          <span style={s.btnRow}>Get All Vendor Quotes →</span>
        )}
      </button>
    </div>
  </div>
);

export default Page2_BoxConfig;
